/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/** Rent cheques across all leases — GET /re/rent-schedule (status, from, to), PUT /re/rent-schedule/{id}. */

import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';
import { usePermission } from 'auth/usePermission';
import RePage, { FilterBar, LoadError, RePanel, useSnack } from './components/RePage';
import ReStatusChip from './components/ReStatusChip';
import ReTable from './components/ReTable';
import { VocabSelect } from './components/Pickers';
import { DateField, Num } from './components/Widgets';
import ChequeDialog, { nextChequeActions } from './components/ChequeDialog';
import {
  CHEQUE_STATUSES, addDaysIso, formatDate, formatMoney, humanize, round2, todayIso, toNumber, useReList,
} from './api';
import { isAustralia } from '../variant';

export default function RentSchedule() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [snack, notify] = useSnack();
  const { canEdit } = usePermission('re_leases');
  const presetStatus = params.get('status');
  const [status, setStatus] = useState(presetStatus || null);
  const [from, setFrom] = useState(presetStatus ? null : addDaysIso(-7));
  const [to, setTo] = useState(presetStatus ? null : addDaysIso(30));
  const [cheque, setCheque] = useState(null);
  const [target, setTarget] = useState(null);
  const list = useReList('/re/rent-schedule', { status, from, to, sort_by: 'due_date', sort_order: 'asc' }, { pageSize: 50 });
  const today = todayIso();

  const pageTotal = useMemo(() => round2(list.items.reduce((a, c) => a + (toNumber(c.amount) || 0), 0)), [list.items]);

  return (
    <RePage
      title={isAustralia() ? 'Rent payments' : 'Rent cheques'}
      subtitle={isAustralia() ? 'Rent due across every lease' : 'Post-dated cheques across every lease'}
    >
      <FilterBar>
        <VocabSelect label="Status" value={status} onChange={setStatus} values={CHEQUE_STATUSES} />
        <DateField label="Due from" value={from} onChange={setFrom} />
        <DateField label="Due to" value={to} onChange={setTo} />
        <MDButton size="small" variant="text" color="error" onClick={() => { setStatus('BOUNCED'); setFrom(null); setTo(null); }}>Bounced only</MDButton>
        <MDButton size="small" variant="text" color="info" onClick={() => { setStatus('PENDING'); setFrom(null); setTo(today); }}>Pending, due by today</MDButton>
      </FilterBar>
      <LoadError error={list.error} onRetry={list.reload} />
      <RePanel p={0}>
        <ReTable
          loading={list.loading}
          rows={list.items}
          emptyMessage={isAustralia() ? 'No rent payments in this range.' : 'No cheques in this range.'}
          total={list.total}
          page={list.page}
          pageSize={list.pageSize}
          onPageChange={list.setPage}
          onPageSizeChange={list.setPageSize}
          rowSx={(c) => (c.status === 'BOUNCED' ? { bgcolor: '#FFF5F4' } : (c.status === 'PENDING' && c.due_date < today ? { bgcolor: '#FFF8EC' } : {}))}
          columns={[
            { key: 'due_date', label: 'Due', render: (c) => formatDate(c.due_date), footer: `Page total (${list.items.length})` },
            {
              key: 'lease', label: 'Lease',
              render: (c) => (
                <MDButton size="small" variant="text" color="info" sx={{ p: 0, minWidth: 0 }} onClick={() => navigate(`/real-estate/leases/${c.lease_id}`)}>
                  {c.lease_number || `Lease #${c.lease_id}`}
                </MDButton>
              ),
            },
            { key: 'unit', label: 'Unit', render: (c) => [c.unit_number, c.project_name].filter(Boolean).join(' · ') || '—' },
            { key: 'tenant', label: 'Tenant', render: (c) => c.tenant_name || '—' },
            { key: 'sequence', label: 'No.', render: (c) => c.sequence },
            { key: 'amount', label: 'Amount', align: 'right', render: (c) => <Num>{formatMoney(c.amount, c.currency)}</Num>, footer: <Num bold>{formatMoney(pageTotal)}</Num> },
            ...(isAustralia() ? [] : [
              { key: 'cheque_no', label: 'Cheque', render: (c) => [c.cheque_no, c.bank_name].filter(Boolean).join(' · ') || '—' },
            ]),
            {
              key: 'status', label: 'Status',
              render: (c) => (
                <MDBox>
                  <ReStatusChip vocab="cheque" status={c.status} size="xs" />
                  {c.status === 'BOUNCED' && c.bounce_reason && <MDTypography variant="caption" color="error" display="block">{c.bounce_reason}</MDTypography>}
                </MDBox>
              ),
            },
            {
              key: 'actions', label: '', align: 'right',
              render: (c) => (canEdit ? (
                <MDBox display="flex" gap={0.5} justifyContent="flex-end">
                  {nextChequeActions(c.status).map((s) => (
                    <MDButton key={s} size="small" variant="text" color={s === 'BOUNCED' ? 'error' : 'info'} onClick={() => { setTarget(s); setCheque(c); }}>
                      {humanize(s)}
                    </MDButton>
                  ))}
                </MDBox>
              ) : null),
            },
          ]}
        />
      </RePanel>
      <ChequeDialog
        cheque={cheque}
        targetStatus={target}
        onClose={() => setCheque(null)}
        onDone={(s) => { setCheque(null); notify(`${isAustralia() ? 'Payment' : 'Cheque'} marked ${humanize(s).toLowerCase()}`); list.reload(); }}
      />
      {snack}
    </RePage>
  );
}
